import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Container, Divider, Title, Box } from '@firsttable/web-components/atoms';
import Image from 'next/image';
import ContainerContent from '@firsttable/web-components/molecules/ContainerContent';
import RowHead from '@firsttable/web-components/molecules/RowHead';
import Reviews from '@firsttable/web-components/organisms/Swiper';
import IconBirdCarryingBook from '@firsttable/web-components/atoms/Icon/IconBirdCarryingBook';
import { TrackProvider } from '@components/analytics/context/AnalyticsContext';
import MemberCard from '../molecules/MemberCard';
import SEO from '../SEO';
import parseHtml from '../../lib/parseHtml';
import NavLink from '../GatsbyLinkConvertor';
import SilverstripeContext from '../context/Silverstripe';

const images = {
  whyWeStarted: {
    path: '/images/why-we-started.svg',
    width: 460,
    height: 548,
  },
  ourStartup: {
    path: '/images/our-startup-story.svg',
    width: 463,
    height: 604,
  },
  weStandFor: {
    path: '/images/what-we-stand-for.svg',
    width: 460,
    height: 422,
  },
  ourVision: {
    path: '/images/our-vision.svg',
    width: 460,
    height: 396,
  },
  smallBirdBigIdea: {
    path: '/images/early-bird.svg',
    width: 460,
    height: 396,
  },
  ourValues: {
    path: '/images/our-values.svg',
    width: 460,
    height: 460,
  },
};

const reviews = [
  {
    id: 1819,
    comment:
      'If you want to take a risk on some new cuisine, eat somewhere that would usually be out of your price range, or make eating out more of a routine than a luxury, then I cannot recommend First Table highly enough.',
    name: 'Sean Stack',
    restaurant: {
      title: 'Portlander Bar and Grill',
    },
  },
  {
    id: 1820,
    role: 'Restaurant Manager',
    comment:
      'First Table has allowed us to reach a wider audience, enabling diners that may not have thought to dine with us previously the chance to experience Pilu at Freshwater at a lesser cost. Our hope is that diners will enjoy a great experience and return to celebrate future special occasions.',
    name: 'Alex Tuckett',
    restaurant: {
      title: 'Pilu at Freshwater',
    },
  },
  {
    id: 1821,
    comment:
      'This is what I love about First Table the most – these places we discover that we never would have found otherwise. Like this place! Wow!',
    name: 'Chassy Holden',
    restaurant: {
      title: 'Sweat Shop Brew Kitchen',
    },
  },
  {
    id: 1822,
    role: 'Owner',
    comment:
      'We are loving First Table! It is a great model that benefits the restaurateur and the customer (very rare!).',
    name: 'Liv Reynolds',
    restaurant: {
      title: 'Mamacita Restaurant',
    },
  },
  {
    id: 1823,
    comment:
      'Amazing, not only was the restaurant and food wonderful... the whole process was easy and such a fantastic idea. Loved every moment and would highly recommend.',
    name: 'Sarah Lane',
    restaurant: {
      title: 'Tamarind Restaurant',
    },
  },
  {
    id: 1824,
    role: 'General Manager',
    comment:
      'I must admit I was sceptical at first about this opportunity, however, after talking with the First Table team it became apparent how much of a difference they could make to our dining numbers. People attract people and by ensuring you have guests in your restaurant early it’s amazing the incremental business you can make from walk-in guests who see other guests enjoying themselves.',
    name: 'Leigh Stock',
    restaurant: {
      title: 'McGavins Restaurant',
    },
  },
];

const BlockImage = ({ imageKey, title }) => {
  const image = images[imageKey];

  if (!image) {
    return null;
  }

  return (
    <Box maxWidth={['100%', 460]}>
      <Image
        src={image.path}
        width={image.width}
        height={image.height}
        alt={title}
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </Box>
  );
};

BlockImage.propTypes = {
  imageKey: PropTypes.string,
  title: PropTypes.string,
};

const MissionBlock = ({
  title,
  children,
  position = 'right',
  imageKey,
}: {
  title: string;
  children: any;
  position?: string;
  imageKey: any;
}) => (
  <>
    <Row gap={40} mt={['m', 80]} mb={['m', 80, 120]} alignItems="center">
      {position === 'left' && (
        <Col width={[1, 1, 1 / 2]} textAlign="center" mb={['l', 0]}>
          <BlockImage imageKey={imageKey} title={title} />
        </Col>
      )}
      <Col width={[1, 1, 1 / 2]} order={[1, 0]}>
        <Box maxWidth={[null, 460]} mx="auto">
          <Title textAlign={['center', 'left']}>{title}</Title>
          {children}
        </Box>
      </Col>
      {position === 'right' && (
        <Col width={[1, 1, 1 / 2]} textAlign="center" mb={['l', 0]}>
          <BlockImage imageKey={imageKey} title={title} />
        </Col>
      )}
    </Row>
    <Divider display={[null, 'none']} mb="xl" />
  </>
);

MissionBlock.propTypes = {
  title: PropTypes.string,
  imageKey: PropTypes.string,
  children: PropTypes.any,
  position: PropTypes.string,
};

type TeamContentProps = {
  members: {
    name: string;
    position: string;
    image: string;
  }[];
};

const TeamContent = ({ members }: TeamContentProps) => (
  <>
    <Container display={[null, 'none']} mb="xl">
      <Divider />
    </Container>
    <Title textAlign="center" size="h1">
      Our Team
    </Title>
    <Container maxWidth={1100} mt={['xl', '60px']} mb={['xl', '60px']}>
      <Row gap={[20, 0]} mb={-30} justifyContent="center">
        {members.map(({ name, position, ...rest }) => (
          <Col key={name + position} width={[1 / 2, 1 / 2, 1 / 3]} mb="xxl">
            <TrackProvider component_name="member card">
              <MemberCard
                NavLinkComponent={NavLink}
                name={name}
                data={null}
                post={position}
                {...rest}
              />
            </TrackProvider>
          </Col>
        ))}
      </Row>
    </Container>
  </>
);

TeamContent.propTypes = {
  data: PropTypes.shape({}),
};

const OurStoryPage = () => {
  const appState = useContext(SilverstripeContext);

  const { page: ourStoryData } = appState;
  const reviewEdges = [];
  reviews.forEach((review) => {
    const node = {
      ...review,
    };
    reviewEdges.push({ node });
  });

  const blocks = ourStoryData.blocks.edges.map(({ node }) => node);

  const missionBlocks = blocks.filter(({ content }) => !!content);

  // const teamMemberBlocks = blocks
  //   .filter(({ content }) => !content)
  //   .map(({ title, key, value }) => ({
  //     name: title,
  //     position: key,
  //     image: awsImage(`/${JSON.parse(value)}`, 300, 390),
  //   }));

  return (
    <TrackProvider pageEventName="marketing_page_viewed" source_page="our story">
      <SEO
        id={ourStoryData.id}
        title={ourStoryData.metaTitleFormatted}
        description={ourStoryData.metaDescription}
        ogImage={ourStoryData.ogImage}
        metaTags={ourStoryData.metaTags}
        slug="/our-story/"
      />
      <ContainerContent>
        <RowHead
          title="Our Story"
          subTitle="We want to share our love of dining out with you. It’s simple."
          icon={<IconBirdCarryingBook />}
        />
        {missionBlocks.map((block, index) => (
          <MissionBlock
            key={block.id}
            imageKey={block.key}
            title={block.title}
            position={index % 2 === 0 ? 'right' : 'left'}>
            {parseHtml(block.content || '')}
          </MissionBlock>
        ))}
      </ContainerContent>
      <Box mb="xl">
        <Container>
          <Title display="inline" mb={['l', null, 0]}>
            What people are saying
          </Title>
        </Container>
        <Reviews reviews={reviewEdges} />
      </Box>
      {/**
         <TrackProvider section="team members section">
          <TeamContent members={teamMemberBlocks} />
         </TrackProvider>
         */}
    </TrackProvider>
  );
};

OurStoryPage.propTypes = {
  data: PropTypes.shape({}),
};

export default OurStoryPage;
